(() => {
  const selector = [
    ':is(.template-collection, .template-search) .collection-filters__filter-list[data-accordion-state="open"]',
    ':is(.template-collection, .template-search) .productgrid--sidebar-item[data-accordion-state="open"]',
    ':is(.template-collection, .template-search) .utils-sortby--modal-list'
  ].join(',');

  let frame = 0;

  const update = (scroller, track) => {
    if (!scroller.isConnected || !track.isConnected) return;

    const overflowAmount = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const overflowTolerance = window.matchMedia('(min-width: 860px)').matches ? 8 : 2;
    const overflow = overflowAmount > overflowTolerance;

    track.hidden = !overflow;
    track.style.display = overflow ? '' : 'none';
    scroller.classList.toggle('jdc-filter-scrollbox', overflow);

    if (!overflow) {
      track.style.removeProperty('top');
      track.style.removeProperty('height');
      return;
    }

    const inset = 8;
    const trackHeight = Math.max(0, scroller.clientHeight - (inset * 2));
    const thumb = track.firstElementChild;
    const thumbHeight = Math.max(32, Math.round(trackHeight * scroller.clientHeight / scroller.scrollHeight));
    const maxThumbTravel = Math.max(0, trackHeight - thumbHeight);
    const maxScroll = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
    const thumbOffset = Math.round(maxThumbTravel * scroller.scrollTop / maxScroll);

    track.style.top = `${scroller.offsetTop + inset}px`;
    track.style.height = `${trackHeight}px`;
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbOffset}px)`;
  };

  const setup = (scroller) => {
    const group = scroller.closest('.collection-filters__filter-group, .productgrid--sidebar-group, .utils-sortby--modal');
    if (!group) return;

    group.classList.add('jdc-filter-scroll-group');

    let track = group.querySelector(':scope > .jdc-filter-scrollbar');
    if (!track) {
      track = document.createElement('span');
      track.className = 'jdc-filter-scrollbar';
      track.setAttribute('aria-hidden', 'true');

      const thumb = document.createElement('span');
      thumb.className = 'jdc-filter-scrollbar__thumb';
      track.appendChild(thumb);
      group.appendChild(track);
    }

    track._jdcScroller = scroller;
    scroller._jdcTrack = track;

    if (!scroller.dataset.jdcScrollReady) {
      scroller.dataset.jdcScrollReady = 'true';
      scroller.addEventListener('scroll', () => {
        if (scroller._jdcTrack) update(scroller, scroller._jdcTrack);
      }, { passive: true });
    }

    if (!scroller._jdcResizeObserver && 'ResizeObserver' in window) {
      scroller._jdcResizeObserver = new ResizeObserver(() => {
        if (scroller._jdcTrack) update(scroller, scroller._jdcTrack);
      });
      scroller._jdcResizeObserver.observe(scroller);
    }

    update(scroller, track);
  };

  const syncFilterModals = () => {
    document.querySelectorAll('.modal[data-modal-container]').forEach((modal) => {
      const isFilterModal = Boolean(modal.querySelector('.productgrid--sidebar, .jdc-filter-panel__title, .filter-form'));
      modal.classList.toggle('jdc-filter-modal', isFilterModal);

      const close = modal.querySelector('[data-modal-close]');
      if (close) {
        close.classList.toggle('jdc-filter-modal__close', isFilterModal);
        if (isFilterModal) {
          close.hidden = false;
          close.setAttribute('aria-hidden', 'false');
        }
      }
    });
  };

  let searchStickyState = null;
  let searchSortModalState = null;

  const setSearchSort = (value) => {
    if (!value) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const previewSearchSortSelection = (source, selectedButton) => {
    source.querySelectorAll('[data-productgrid-trigger-sort-button]').forEach((button) => {
      const active = button === selectedButton;
      button.classList.toggle('utils-sortby--modal-button--active', active);
      button.disabled = active;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const closeSearchSortModal = () => {
    if (!searchSortModalState) return;

    const {
      dialog,
      source,
      placeholder,
      trigger,
      scrollY,
      previousBodyTop,
    } = searchSortModalState;

    dialog.setAttribute('aria-hidden', 'true');
    source.setAttribute('aria-hidden', 'true');
    if (placeholder.isConnected) {
      placeholder.replaceWith(dialog);
    }
    document.body.classList.remove('jdc-search-sort-open');
    document.documentElement.classList.remove('scroll-locked');
    document.body.style.top = previousBodyTop;
    window.scrollTo(0, scrollY);

    searchSortModalState = null;
    if (trigger && trigger.isConnected) trigger.focus();
  };

  const openSearchSortModal = (trigger) => {
    if (!window.matchMedia('(max-width: 859px)').matches || searchSortModalState) return;

    const section = trigger.closest('.search--section');
    const dialog = section?.querySelector('[data-jdc-search-sort-dialog]');
    const source = dialog?.querySelector('[data-productgrid-sort-content]');

    if (!dialog || !source) return;

    const placeholder = document.createComment('jdc-search-sort-dialog-origin');
    const scrollY = window.scrollY;
    const previousBodyTop = document.body.style.top;

    dialog.before(placeholder);
    document.body.appendChild(dialog);
    dialog.setAttribute('aria-hidden', 'false');
    source.setAttribute('aria-hidden', 'false');
    document.body.classList.add('jdc-search-sort-open');
    document.documentElement.classList.add('scroll-locked');
    document.body.style.top = `-${scrollY}px`;

    searchSortModalState = {
      dialog,
      source,
      placeholder,
      trigger,
      scrollY,
      previousBodyTop,
      navigating: false,
    };

    requestAnimationFrame(() => {
      const firstChoice = source.querySelector('[data-productgrid-trigger-sort-button]:not([disabled])');
      const closeButton = dialog.querySelector('[data-jdc-search-sort-close]');
      (firstChoice || closeButton)?.focus();
    });
  };

  const handleSearchUtilitiesClick = (event) => {
    if (!document.body.classList.contains('template-search')) return;

    const sortTrigger = event.target.closest('[data-productgrid-modal-sort]');
    if (sortTrigger) {
      event.preventDefault();
      openSearchSortModal(sortTrigger);
      return;
    }

    if (!searchSortModalState) return;

    const sortChoice = event.target.closest('[data-productgrid-trigger-sort-button]');
    if (sortChoice && !sortChoice.disabled && !searchSortModalState.navigating) {
      event.preventDefault();
      event.stopImmediatePropagation();
      searchSortModalState.navigating = true;
      previewSearchSortSelection(searchSortModalState.source, sortChoice);

      window.setTimeout(() => {
        setSearchSort(sortChoice.value);
      }, 90);
      return;
    }

    if (event.target.closest('[data-jdc-search-sort-close]')) {
      event.preventDefault();
      closeSearchSortModal();
    }
  };

  const handleSearchUtilitiesChange = (event) => {
    if (!document.body.classList.contains('template-search')) return;

    const select = event.target.closest('[data-productgrid-trigger-sort]');
    if (select) setSearchSort(select.value);
  };

  const handleSearchUtilitiesKeydown = (event) => {
    if (!searchSortModalState) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearchSortModal();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(searchSortModalState.dialog.querySelectorAll(
      'button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const cleanupSearchSticky = () => {
    if (!searchStickyState) return;

    searchStickyState.observer?.disconnect();
    searchStickyState.utils.classList.remove('productgrid--utils-box-shadow');
    searchStickyState.utils.style.removeProperty('top');
    searchStickyState.target.style.removeProperty('top');
    searchStickyState = null;
  };

  const syncSearchSticky = () => {
    const mobile = window.matchMedia('(max-width: 859px)').matches;
    const section = document.querySelector('.search--section');
    const utils = section?.querySelector('[data-sticky-utils]');
    const target = section?.querySelector('[data-utils-intersection-target]');

    if (!mobile || !section || !utils || !target) {
      cleanupSearchSticky();
      return;
    }

    const header = document.querySelector('[data-site-header-main]');
    const headerHeight = document.body.classList.contains('site-header-sticky')
      ? (header?.offsetHeight || 0)
      : 0;

    utils.style.top = `${headerHeight}px`;
    target.style.top = `-${headerHeight}px`;

    if (
      searchStickyState
      && searchStickyState.section === section
      && searchStickyState.utils === utils
      && searchStickyState.target === target
    ) {
      return;
    }

    cleanupSearchSticky();

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((records) => {
          records.forEach((record) => {
            const stuck = record.boundingClientRect.bottom < (record.rootBounds?.top || 0);
            utils.classList.toggle('productgrid--utils-box-shadow', stuck);
          });
        })
      : null;

    observer?.observe(target);
    searchStickyState = { section, utils, target, observer };
  };

  document.addEventListener('click', handleSearchUtilitiesClick, true);
  document.addEventListener('change', handleSearchUtilitiesChange);
  window.addEventListener('keydown', handleSearchUtilitiesKeydown);

  const refresh = () => {
    frame = 0;
    syncFilterModals();
    syncSearchSticky();

    const customScrollbarsEnabled = window.matchMedia('(max-width: 859px)').matches;

    document.querySelectorAll('.jdc-filter-scrollbar').forEach((track) => {
      const scroller = track._jdcScroller;
      const shouldRemove = !customScrollbarsEnabled
        || !scroller
        || !scroller.isConnected
        || !scroller.matches(selector);

      if (shouldRemove) {
        if (scroller) {
          scroller.classList.remove('jdc-filter-scrollbox');
          scroller._jdcTrack = null;
        }
        track.remove();
      }
    });

    if (!customScrollbarsEnabled) {
      document.querySelectorAll('.jdc-filter-scrollbox').forEach((scroller) => {
        scroller.classList.remove('jdc-filter-scrollbox');
        scroller._jdcTrack = null;
      });
      return;
    }

    document.querySelectorAll(selector).forEach(setup);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(refresh);
  };

  const start = () => {
    refresh();

    const observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-accordion-state', 'aria-hidden']
    });

    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();